export interface RouletteEntrant {
  id: string;
  nickname?: string;
}

export interface RouletteVote {
  requestId: string;
  oderId: string;
  type?: 'up' | 'down';
  nickname?: string;
}

export interface RouletteCandidate {
  requestId: string;
  entrants: RouletteEntrant[];
}

export function buildRouletteEntrants(
  request: { id: string; requestedBy?: { oderId?: string; nickname?: string } },
  votes: RouletteVote[],
  excludeIds: Set<string>,
): RouletteEntrant[] {
  const entrants = new Map<string, RouletteEntrant>();

  const addEntrant = (id: string | undefined, nickname: string | undefined) => {
    if (!id || excludeIds.has(id)) return;
    const existing = entrants.get(id);
    if (!existing) {
      entrants.set(id, { id, nickname: nickname || undefined });
    } else if (!existing.nickname && nickname) {
      existing.nickname = nickname;
    }
  };

  addEntrant(request.requestedBy?.oderId, request.requestedBy?.nickname);

  for (const vote of votes) {
    if (vote.requestId !== request.id) continue;
    if (vote.type === 'down') continue;
    addEntrant(vote.oderId, vote.nickname);
  }

  return Array.from(entrants.values());
}

export function pickRouletteCandidate(
  candidates: RouletteCandidate[],
  randomIndex: (n: number) => number,
): RouletteCandidate | null {
  const withEntrants = candidates.filter((candidate) => candidate.entrants.length > 0);
  if (withEntrants.length === 0) return null;
  return withEntrants[randomIndex(withEntrants.length)];
}

export function pickRouletteWinner(
  entrants: RouletteEntrant[],
  randomIndex: (n: number) => number,
): RouletteEntrant {
  if (entrants.length === 0) {
    throw new Error('pickRouletteWinner needs at least one entrant');
  }
  if (entrants.length === 1) return entrants[0];
  return entrants[randomIndex(entrants.length)];
}

export function cryptoRandomIndex(n: number): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] % n;
}
